import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUserProgram } from '@/hooks/useUserProgram';
import { supabase } from '@/integrations/supabase/client';

// Re-export types for consumers
export interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  content?: string;
  created_at?: string;
}

export interface AudioFile {
  id: string;
  title: string;
  description: string;
  category: string;
  audio_url: string;
  duration: number;
  created_at: string;
}

export interface VideoFile {
  id: string;
  title: string;
  description: string;
  category: string;
  video_url: string;
  duration: number;
  created_at: string;
  is_vr: boolean;
  vr_projection: string;
  vr_stereo_mode: string;
}

export interface Assessment {
  id: string;
  title: string;
  description: string;
  created_at: string;
}

/**
 * Hook that returns only content that is part of the user's active program.
 * Fetches content directly by IDs from program phases, bypassing product_content junction.
 * If no active program is selected, returns empty arrays.
 */
export function useProgramContent() {
  const { phases, program, loading: programLoading } = useUserProgram();

  // Extract activity IDs from all phases, grouped by type
  const programActivityIds = useMemo(() => {
    const ids: Record<string, string[]> = {
      article: [],
      video: [],
      audio: [],
      assessment: [],
    };

    for (const phase of phases) {
      for (const activity of phase.activities) {
        const type = activity.activity_type.toLowerCase();
        if (ids[type]) {
          ids[type].push(activity.activity_id);
        }
      }
    }

    return ids;
  }, [phases]);

  // Fetch articles directly by IDs
  const { data: articles = [], isLoading: articlesLoading } = useQuery({
    queryKey: ['program-content', 'articles', programActivityIds.article],
    queryFn: async (): Promise<Article[]> => {
      if (programActivityIds.article.length === 0) return [];
      const { data, error } = await supabase
        .from('articles')
        .select('id, title, excerpt, category, content, created_at')
        .in('id', programActivityIds.article)
        .eq('published', true);
      if (error) throw error;
      return (data || []).map(a => ({
        id: a.id,
        title: a.title,
        excerpt: a.excerpt || '',
        category: a.category,
        content: a.content,
        created_at: a.created_at || '',
      }));
    },
    enabled: !!program && programActivityIds.article.length > 0,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch videos directly by IDs
  const { data: videos = [], isLoading: videosLoading } = useQuery({
    queryKey: ['program-content', 'videos', programActivityIds.video],
    queryFn: async (): Promise<VideoFile[]> => {
      if (programActivityIds.video.length === 0) return [];
      const { data, error } = await supabase
        .from('video_files')
        .select('id, title, description, category, video_url, duration, created_at, is_vr, vr_projection, vr_stereo_mode')
        .in('id', programActivityIds.video)
        .eq('published', true);
      if (error) throw error;
      return (data || []).map(v => ({
        id: v.id,
        title: v.title,
        description: v.description || '',
        category: v.category,
        video_url: v.video_url || '',
        duration: v.duration || 0,
        created_at: v.created_at || '',
        is_vr: v.is_vr || false,
        vr_projection: v.vr_projection || 'equirectangular',
        vr_stereo_mode: v.vr_stereo_mode || 'mono',
      }));
    },
    enabled: !!program && programActivityIds.video.length > 0,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch audio directly by IDs
  const { data: audio = [], isLoading: audioLoading } = useQuery({
    queryKey: ['program-content', 'audio', programActivityIds.audio],
    queryFn: async (): Promise<AudioFile[]> => {
      if (programActivityIds.audio.length === 0) return [];
      const { data, error } = await supabase
        .from('audio_files')
        .select('id, title, description, category, audio_url, duration, created_at')
        .in('id', programActivityIds.audio)
        .eq('published', true);
      if (error) throw error;
      return (data || []).map(a => ({
        id: a.id,
        title: a.title,
        description: a.description || '',
        category: a.category,
        audio_url: a.audio_url || '',
        duration: a.duration || 0,
        created_at: a.created_at || '',
      }));
    },
    enabled: !!program && programActivityIds.audio.length > 0,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch assessments directly by IDs
  const { data: assessments = [], isLoading: assessmentsLoading } = useQuery({
    queryKey: ['program-content', 'assessments', programActivityIds.assessment],
    queryFn: async (): Promise<Assessment[]> => {
      if (programActivityIds.assessment.length === 0) return [];
      const { data, error } = await supabase
        .from('assessments')
        .select('id, title, description, created_at')
        .in('id', programActivityIds.assessment)
        .eq('published', true);
      if (error) throw error;
      return (data || []).map(a => ({
        id: a.id,
        title: a.title,
        description: a.description || '',
        created_at: a.created_at || '',
      }));
    },
    enabled: !!program && programActivityIds.assessment.length > 0,
    staleTime: 1000 * 60 * 5,
  });

  const contentLoading = articlesLoading || videosLoading || audioLoading || assessmentsLoading;

  return {
    articles,
    videos,
    audio,
    assessments,
    program,
    hasActiveProgram: !!program,
    isLoading: programLoading || contentLoading,
  };
}
